import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, X, ArrowLeft, Camera, Image } from 'lucide-react';
import { getCountries, getStates, getCities } from '@/utils/locationData';
import { SearchableSelect } from '@/components/SearchableSelect';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EditListingPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'Electronics',
    condition: 'good',
    city: '',
    state: '',
    pin_code: '',
    country: '',
    delivery_preferences: '',
    bidding_end_time: '',
    preferences: {
      preferred_categories: [],
      specific_items: '',
      money_allowed: true,
      only_barter: false,
      only_money: false,
      min_money_required: 0,
      acceptable_money_range_min: 0,
      acceptable_money_range_max: 0
    },
    images: []
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  // Location states
  const [countries] = useState(getCountries());
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const categories = ['Electronics', 'Fashion', 'Furniture', 'Home', 'Vehicles', 'Books', 'Sports', 'Food', 'Pets & Plants', 'Coupons', 'Services', 'Other'];
  const conditions = [
    { value: 'new', label: 'New' },
    { value: 'like_new', label: 'Like New' },
    { value: 'good', label: 'Good' },
    { value: 'fair', label: 'Fair' },
    { value: 'poor', label: 'Poor' }
  ];

  // Fetch listing data
  useEffect(() => {
    const fetchListing = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/listings/${id}`);
        const listing = response.data;
        
        setFormData({
          title: listing.title || '',
          description: listing.description || '',
          category: listing.category || 'Electronics',
          condition: listing.condition || 'good',
          city: listing.city || '',
          state: listing.state || '',
          pin_code: listing.pin_code || '',
          country: listing.country || '',
          delivery_preferences: listing.delivery_preferences || '',
          bidding_end_time: listing.bidding_end_time || '',
          preferences: listing.preferences || {
            preferred_categories: [],
            specific_items: '',
            money_allowed: true,
            only_barter: false,
            only_money: false,
            min_money_required: 0,
            acceptable_money_range_min: 0,
            acceptable_money_range_max: 0
          },
          images: listing.images || []
        });

        // Initialize states if country is set
        if (listing.country) {
          setStates(getStates(listing.country));
          if (listing.state) {
            setCities(getCities(listing.country, listing.state));
          }
        }
      } catch (error) {
        console.error('Failed to fetch listing', error);
        toast.error('Failed to load listing');
        navigate('/my-listings');
      } finally {
        setFetching(false);
      }
    };

    fetchListing();
  }, [id, navigate]);

  // Update states when country changes
  useEffect(() => {
    if (formData.country && !fetching) {
      const countryStates = getStates(formData.country);
      setStates(countryStates);
    }
  }, [formData.country, fetching]);

  // Update cities when state changes
  useEffect(() => {
    if (formData.country && formData.state && !fetching) {
      const stateCities = getCities(formData.country, formData.state);
      setCities(stateCities);
    }
  }, [formData.country, formData.state, fetching]);

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          images: [...prev.images, reader.result]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const toggleCategory = (category) => {
    const newCategories = formData.preferences.preferred_categories.includes(category)
      ? formData.preferences.preferred_categories.filter((c) => c !== category)
      : [...formData.preferences.preferred_categories, category];
    setFormData({
      ...formData,
      preferences: { ...formData.preferences, preferred_categories: newCategories }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.put(`${API_URL}/api/listings/${id}`, formData);
      toast.success('Listing updated successfully!');
      navigate('/my-listings');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update listing');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/my-listings')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to My Listings
        </button>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Edit Listing
          </h1>
          <p className="text-slate-600 mb-8">Update your listing details</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Title *</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 h-12 px-4"
                placeholder="What are you trading?"
                data-testid="listing-title-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Description *</label>
              <textarea
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 p-4"
                rows="4"
                placeholder="Describe your item in detail..."
                data-testid="listing-description-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 h-12 px-4"
                  data-testid="listing-category-select"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Condition *</label>
                <select
                  value={formData.condition}
                  onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                  className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 h-12 px-4"
                  data-testid="listing-condition-select"
                >
                  {conditions.map((cond) => (
                    <option key={cond.value} value={cond.value}>
                      {cond.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Country</label>
                <SearchableSelect
                  options={countries}
                  value={formData.country}
                  onChange={(value) => setFormData({ ...formData, country: value, state: '', city: '' })}
                  placeholder="Select Country"
                  testId="listing-country-select"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">State</label>
                <SearchableSelect
                  options={states}
                  value={formData.state}
                  onChange={(value) => setFormData({ ...formData, state: value, city: '' })}
                  placeholder="Select State"
                  disabled={!formData.country}
                  testId="listing-state-select"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">City</label>
                <SearchableSelect
                  options={cities}
                  value={formData.city}
                  onChange={(value) => setFormData({ ...formData, city: value })}
                  placeholder="Select City"
                  disabled={!formData.state}
                  testId="listing-city-select"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Pin Code</label>
                <input
                  type="text"
                  value={formData.pin_code}
                  onChange={(e) => setFormData({ ...formData, pin_code: e.target.value })}
                  className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 h-12 px-4"
                  placeholder="Pin code"
                  data-testid="listing-pincode-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Bidding End Date *</label>
                <input
                  type="datetime-local"
                  required
                  value={formData.bidding_end_time ? new Date(formData.bidding_end_time).toISOString().slice(0, 16) : ''}
                  onChange={(e) => setFormData({ ...formData, bidding_end_time: new Date(e.target.value).toISOString() })}
                  className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 h-12 px-4"
                  data-testid="listing-end-time-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Delivery Preferences</label>
              <input
                type="text"
                value={formData.delivery_preferences}
                onChange={(e) => setFormData({ ...formData, delivery_preferences: e.target.value })}
                className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 h-12 px-4"
                placeholder="Pickup, shipping, local delivery, etc."
                data-testid="listing-delivery-input"
              />
            </div>

            {/* Images */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Images</label>
              
              {/* Existing images */}
              {formData.images.length > 0 && (
                <div className="grid grid-cols-4 gap-4 mb-4">
                  {formData.images.map((img, index) => (
                    <div key={index} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200">
                      <img src={img} alt={`Image ${index + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                        data-testid={`remove-image-${index}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="border-2 border-dashed border-slate-300 rounded-xl p-6">
                <div className="text-center mb-4">
                  <Upload className="w-12 h-12 text-slate-400 mx-auto mb-2" />
                  <p className="text-slate-600">Add more images</p>
                </div>
                
                <div className="flex flex-wrap justify-center gap-3">
                  {/* File Upload */}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="hidden"
                    id="image-upload"
                    data-testid="image-upload-input"
                  />
                  <label
                    htmlFor="image-upload"
                    className="inline-flex items-center gap-2 cursor-pointer bg-indigo-600 text-white px-5 py-2.5 rounded-full font-medium hover:bg-indigo-700 transition-colors"
                  >
                    <Image className="w-5 h-5" />
                    Choose Files
                  </label>
                  
                  {/* Camera Capture */}
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="camera-capture"
                    data-testid="camera-capture-input"
                  />
                  <label
                    htmlFor="camera-capture"
                    className="inline-flex items-center gap-2 cursor-pointer bg-green-600 text-white px-5 py-2.5 rounded-full font-medium hover:bg-green-700 transition-colors"
                  >
                    <Camera className="w-5 h-5" />
                    Take Photo
                  </label>
                </div>
              </div>
            </div>

            {/* Trading Preferences */}
            <div className="border-t border-slate-200 pt-6">
              <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                What do you want in exchange?
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Specific items you want</label>
                  <textarea
                    value={formData.preferences.specific_items || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        preferences: { ...formData.preferences, specific_items: e.target.value }
                      })
                    }
                    className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 p-4"
                    rows="2"
                    placeholder="e.g., iPhone 13, PlayStation 5, MacBook..."
                    data-testid="specific-items-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">Preferred categories</label>
                  <div className="flex flex-wrap gap-2">
                    {categories.map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => toggleCategory(cat)}
                        className={`px-4 py-2 rounded-full font-medium transition-colors ${
                          formData.preferences.preferred_categories?.includes(cat)
                            ? 'bg-indigo-600 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                        data-testid={`category-toggle-${cat}`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Trade options</label>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="trade-option"
                        checked={formData.preferences.money_allowed && !formData.preferences.only_barter && !formData.preferences.only_money}
                        onChange={() =>
                          setFormData({
                            ...formData,
                            preferences: {
                              ...formData.preferences,
                              money_allowed: true,
                              only_barter: false,
                              only_money: false
                            }
                          })
                        }
                        className="w-4 h-4 text-indigo-600"
                        data-testid="both-option-radio"
                      />
                      <span className="text-slate-700 font-medium">Accept both money and barter</span>
                    </label>

                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="trade-option"
                        checked={formData.preferences.only_barter}
                        onChange={() =>
                          setFormData({
                            ...formData,
                            preferences: {
                              ...formData.preferences,
                              money_allowed: false,
                              only_barter: true,
                              only_money: false
                            }
                          })
                        }
                        className="w-4 h-4 text-indigo-600"
                        data-testid="barter-only-radio"
                      />
                      <span className="text-slate-700 font-medium">Barter only (no money)</span>
                    </label>

                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="trade-option"
                        checked={formData.preferences.only_money}
                        onChange={() =>
                          setFormData({
                            ...formData,
                            preferences: {
                              ...formData.preferences,
                              money_allowed: true,
                              only_barter: false,
                              only_money: true
                            }
                          })
                        }
                        className="w-4 h-4 text-indigo-600"
                        data-testid="money-only-radio"
                      />
                      <span className="text-slate-700 font-medium">Money only (no barter)</span>
                    </label>
                  </div>
                </div>

                {(formData.preferences.money_allowed || formData.preferences.only_money) && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Min money</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.preferences.acceptable_money_range_min || 0}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            preferences: {
                              ...formData.preferences,
                              acceptable_money_range_min: parseFloat(e.target.value) || 0
                            }
                          })
                        }
                        className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 h-12 px-4"
                        data-testid="min-money-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Max money</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.preferences.acceptable_money_range_max || 0}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            preferences: {
                              ...formData.preferences,
                              acceptable_money_range_max: parseFloat(e.target.value) || 0
                            }
                          })
                        }
                        className="w-full rounded-xl border-slate-200 bg-slate-50 focus:bg-white focus:ring-2 focus:ring-indigo-500 h-12 px-4"
                        data-testid="max-money-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex space-x-4 pt-6">
              <button
                type="button"
                onClick={() => navigate('/my-listings')}
                className="flex-1 rounded-full font-bold px-8 py-3 border-2 border-slate-200 hover:border-slate-900 bg-transparent text-slate-900 transition-colors"
                data-testid="cancel-button"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 rounded-full font-bold px-8 py-3 bg-indigo-600 text-white hover:bg-indigo-700 transition-all active:scale-95 shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 disabled:opacity-50"
                data-testid="update-listing-button"
              >
                {loading ? 'Updating...' : 'Update Listing'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
